import { DataAPIService, ProfileAPIService } from './AxiosAPIService';
import {
  Driver,
  DriverDetails, DriverScores,
  DriversCountResponse,
  DriversResponse, DriversScores,
  PostDriverRequest,
  PatchDriverRequest,
  PostLearnFaceRequest,
  DeleteLearnFaceRequest,
  DeleteDriverRequest,
  DeleteDriversRequest,
  DriversTopScoresRequest,
  DriversTopScoresResponse,
  DriversScoresAverage,
  DriversHistoryResponse,
  DriversStatisticsByVehicleResponse,
  DHTLimitsResponse,
  DHTLimits,
  DriverAvatar,
  DriverScoreDay,
  DriverScoreYear,
  DriverScoreYears,
  DriversAvatarRequest,
  DHTCounterResponse,
  PostDriverAvatarRequest,
  DriverAvatarRequest,
  DriverAlertsCount,
} from '../types/Drivers';
import { logger, normalizeQueryParams } from './SharedService';

const DRIVERS_ENDPOINT = '/fleets';

export const getDrivers = async (fleetId: string, query: string): Promise<DriversResponse> => {
  const { data } = await ProfileAPIService.api
    .get<DriversResponse>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers${query}`);
  return data;
};

export const getDriverById = async (id: string, fleetId: string): Promise<DriverDetails> => {
  const { data } = await ProfileAPIService.api
    .get<DriverDetails>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/${id}`);
  return data;
};

export const getDriversCount = async (fleetId: string): Promise<DriversCountResponse> => {
  const { data } = await ProfileAPIService.api
    .get<DriversCountResponse>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/aggregation`);
  return data;
};

export const getDriversTopScores = async (fleetId: string, query = ''): Promise<DriversTopScoresResponse> => {
  const normalizedQuery = normalizeQueryParams(query);
  const { data } = await DataAPIService.api
    .get<DriversTopScoresResponse>(`${fleetId}/drivers/scores/top${normalizedQuery}`);
  return data;
};

export const postDriver = async (payload: PostDriverRequest): Promise<Driver> => {
  const {
    fleetId, firstName, lastName, email, phoneNumber, licenseName, licenseExpireAt, workingHours, file,
  } = payload;

  const input = new FormData();
  input.append('firstName', firstName);
  input.append('lastName', lastName);
  input.append('email', email);
  input.append('phoneNumber', phoneNumber);
  input.append('licenseName', licenseName);
  input.append('licenseExpireAt', (new Date(licenseExpireAt)).toString());
  // input.append('workingHours', workingHours);
  input.append('file', file);

  const { data } = await ProfileAPIService.api
    .post<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers`, input, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const patchDriver = async (payload: PatchDriverRequest): Promise<Driver> => {
  const {
    id, fleetId, firstName, lastName, email, phoneNumber, licenseName, licenseExpireAt, workingHours, vehicleClassType, file, deleteAvatar,
  } = payload;

  const input = new FormData();
  input.append('firstName', firstName);
  input.append('lastName', lastName);
  input.append('email', email);
  input.append('phoneNumber', phoneNumber);
  input.append('licenseName', licenseName);
  input.append('licenseExpireAt', (new Date(licenseExpireAt)).toString());
  // input.append('workingHours', workingHours);
  // input.append('vehicleClassType', vehicleClassType);
  if (file !== undefined) {
    input.append('file', file);
  }
  if (deleteAvatar) {
    input.append('deleteAvatar', 'true');
  }
  const { data } = await ProfileAPIService.api
    .patch<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/${id}`, input, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const deleteDriver = async (payload: DeleteDriverRequest): Promise<Driver> => {
  const { fleetId, driverId } = payload;
  const { data } = await ProfileAPIService.api
    .delete<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/${driverId}`);
  return data;
};

export const deleteDrivers = async (payload: DeleteDriversRequest): Promise<Driver[]> => {
  const deletePromises = payload?.ids?.map(async (id) => {
    const { data } = await ProfileAPIService.api.delete<Driver>(`${DRIVERS_ENDPOINT}/${payload.fleetId}/drivers/${id}`);
    return data;
  });

  const results = await Promise.all(deletePromises);
  return results;
};

export const postLearnDriverFace = async (payload: PostLearnFaceRequest): Promise<Driver> => {
  const {
    fleetId, driverId, deviceId, learnFaceRequestedBy, force,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/learn-face`, {
    driverId,
    deviceId,
    learnFaceRequestedBy,
    force,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const postDriverAvatarRequest = async (payload: PostDriverAvatarRequest): Promise<Driver> => {
  const {
    fleetId, driverId, force,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/${driverId}/avatar-request`, {
    force,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const deleteLearnDriverFace = async (payload: DeleteLearnFaceRequest): Promise<Driver> => {
  const {
    fleetId, driverId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .patch<Driver>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers/${driverId}`, {
    faceData: null,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const getDriverScoreDay = async (
  id: string,
  fleetId: string,
  date: string,
): Promise<DriverScoreDay> => {
  const { data } = await DataAPIService.api
    .get<DriverScoreDay>(`${fleetId}/drivers/${id}/scores/day?date=${date}`);
  return data;
};

export const getDriverScoreYear = async (
  id: string,
  fleetId: string,
  year: number,
): Promise<DriverScoreYear> => {
  const { data } = await DataAPIService.api
    .get<DriverScoreYear>(`${fleetId}/drivers/${id}/scores/year?year=${year}`);
  return data;
};

export const getDriverScoreYears = async (
  id: string,
  fleetId: string,
  from: number,
  to?: number,
): Promise<DriverScoreYears> => {
  const toParam = to ? `&to=${to}` : '';
  const { data } = await DataAPIService.api
    .get<DriverScoreYears>(`${fleetId}/drivers/${id}/scores/years?from=${from}${toParam}`);
  return data;
};

export const getDriverScoresById = async (
  id: string,
  fleetId: string,
  fromDate: string,
  toDate: string,
): Promise<DriverScores> => {
  const { data } = await DataAPIService.api
    .get<DriverScores>(`${fleetId}/drivers/${id}/scores?fromDate=${fromDate}&toDate=${toDate}`);
  return data;
};

export const getDriversScores = async (
  fleetId: string,
  fromDate: string,
  toDate: string,
): Promise<DriversScores> => {
  logger.info('GET', 'REQUEST', 'getDriversScores', `${fleetId}/drivers/scores?fromDate=${fromDate}&toDate=${toDate}`);
  const { data } = await DataAPIService.api
    .get<DriversScores>(`${fleetId}/drivers/scores?fromDate=${fromDate}&toDate=${toDate}`);
  logger.info('GET', 'RESPONSE', 'getDriversScores', data);
  return data;
};

export const getDriversScoresAverage = async (fleetId: string, query = ''): Promise<DriversScoresAverage> => {
  const normalizedQuery = normalizeQueryParams(query);
  logger.info('GET', 'REQUEST', 'getDriversScoresAverage', `${fleetId}/drivers/scores/average${normalizedQuery}`);
  const { data } = await DataAPIService.api
    .get<DriversScoresAverage>(`${fleetId}/drivers/scores/average${normalizedQuery}`);
  logger.info('GET', 'RESPONSE', 'getDriversScoresAverage', data);
  return data;
};

export const getDriversHistory = async (fleetId: string, query: string): Promise<DriversHistoryResponse> => {
  const { data } = await ProfileAPIService.api
    .get<DriversHistoryResponse>(`${DRIVERS_ENDPOINT}/${fleetId}/drivers-history${query}`);
  return data;
};

export const getDriversStatisticsByVehicle = async (fleetId: string, vehicleId: string, query: string): Promise<DriversStatisticsByVehicleResponse> => {
  const { data } = await DataAPIService.api
    .get<DriversStatisticsByVehicleResponse>(`${fleetId}/vehicles/${vehicleId}/statistics${query}`);

  return data;
};

export const getDriversDHTLimits = async (fleetId: string, driverId?: string): Promise<DHTLimitsResponse> => {
  logger.info('GET', 'REQUEST', 'getDriversDHTLimits', `${fleetId}/time-tracker${driverId ? `/drivers/${driverId}` : ''}`);
  const { data } = await DataAPIService.api
    .get<DHTLimitsResponse>(`${fleetId}/time-tracker${driverId ? `/drivers/${driverId}` : ''}`);
  logger.info('GET', 'RESPONSE', 'getDriversDHTLimits', data);
  return data;
};

export const postDriversDHTLimits = async (payload: DHTLimitsResponse): Promise<DHTLimits> => {
  logger.info('POST', 'REQUEST', 'postDriversDHTLimits', `${payload.fleetId}/time-tracker${payload.driverId ? `/drivers/${payload.driverId}` : ''}`, payload);
  const { data } = await DataAPIService.api
    .post<DHTLimits>(`${payload.fleetId}/time-tracker${payload.driverId ? `/drivers/${payload.driverId}` : ''}`, {
    limitTripInSec: payload.limitTripInSec,
    limitDayInSec: payload.limitDayInSec,
    limitWeekInSec: payload.limitWeekInSec,
    limitMonthInSec: payload.limitMonthInSec,
    ...(payload.driverId ? {} : { lockDriverDhtEdit: payload.lockDriverDhtEdit ?? false }),
  });
  logger.info('POST', 'RESPONSE', 'postDriversDHTLimits', data);
  return data;
};

export const patchDriversDHTLimits = async (payload: DHTLimitsResponse): Promise<DHTLimits> => {
  logger.info('PATCH', 'REQUEST', 'patchDriversDHTLimits', `${payload.fleetId}/time-tracker${payload.driverId ? `/drivers/${payload.driverId}` : ''}`, payload);
  const { data } = await DataAPIService.api
    .patch<DHTLimits>(`${payload.fleetId}/time-tracker${payload.driverId ? `/drivers/${payload.driverId}` : ''}`, {
    limitTripInSec: payload.limitTripInSec,
    limitDayInSec: payload.limitDayInSec,
    limitWeekInSec: payload.limitWeekInSec,
    limitMonthInSec: payload.limitMonthInSec,
    ...(payload.driverId ? {} : { lockDriverDhtEdit: payload.lockDriverDhtEdit ?? false }),
  });
  logger.info('PATCH', 'RESPONSE', 'patchDriversDHTLimits', data);
  return data;
};

export const getDriversDHTCounter = async (fleetId: string, driverId: string): Promise<DHTCounterResponse> => {
  logger.info('GET', 'REQUEST', 'getDriversDHTCounter', `${fleetId}/time-tracker/drivers/${driverId}/value`);
  const { data } = await DataAPIService.api
    .get<DHTCounterResponse>(`${fleetId}/time-tracker/drivers/${driverId}/value`);
  logger.info('GET', 'RESPONSE', 'getDriversDHTCounter', data);
  return data;
};

export const getDriverAvatar = async (payload: DriverAvatarRequest): Promise<DriverAvatar> => {
  logger.info('POST', 'REQUEST', 'getDriverAvatar', `fleets/${payload.fleetId}/drivers/${payload.driverId}/avatar-request`);
  const { data } = await ProfileAPIService.api
    .post<DriverAvatar>(`fleets/${payload.fleetId}/drivers/${payload.driverId}/avatar-request`);
  logger.info('POST', 'RESPONSE', 'getDriverAvatar', data);
  return data;
};

export const getDriverAlertsCount = async (fleetId: string, driverId: string, query: string): Promise<DriverAlertsCount> => {
  const { data } = await DataAPIService.api
    .get<DriverAlertsCount>(`${fleetId}/drivers/${driverId}/alerts/count${normalizeQueryParams(query)}`);
  return data;
};

export const getDriversAvatarsBulk = async (payload: DriversAvatarRequest): Promise<DriverAvatar[]> => {
  logger.info('POST', 'REQUEST', 'getDriversAvatarsBulk', `fleets/${payload.fleetId}/drivers/avatar-url-bulk`, payload.driverIds);
  const { data } = await ProfileAPIService.api
    .post<DriverAvatar[]>(`fleets/${payload.fleetId}/drivers/avatar-url-bulk`, { driverIds: payload.driverIds });
  logger.info('POST', 'RESPONSE', 'getDriversAvatarsBulk', data);
  return data;
};
